/**
 * Formatting for the LE/CD tabs.
 *
 * Three conventions from the API contract are enforced here so no component has
 * to remember them:
 *
 * **Money is a string.** The API sends `"286.40"`, never `286.4`, because these
 * are `Decimal` server-side and a JS float cannot hold them faithfully —
 * `286.40` round-trips as `286.39999999999998`. Everything below operates on
 * the string. There is deliberately no `parseFloat` in this file, and no
 * arithmetic helper: if a total is needed, the server already computed it.
 *
 * **`null` means "not recorded", never "no".** `cc_valid`, `from_written_list`
 * and `has_prepayment_penalty` are genuinely tri-state, and rendering unknown
 * as false is a compliance misstatement — "no changed circumstance applies" and
 * "one was cited but was untimely" are different facts about a loan.
 *
 * **Dates are whole days.** TRID deadlines are counted in days, so a date field
 * is a bare `YYYY-MM-DD` and is read by splitting it, never through `new Date`.
 *
 * **Timestamps arrive already offset to the creditor's timezone**, by
 * `business_days.business_stamp` server-side. So the leading `YYYY-MM-DD` of
 * `recorded_at` or `approved_at` is the day the creditor had, and the same split
 * is correct for both kinds. Do not convert one through `new Date` to "fix" the
 * zone: that would re-answer the question in the *browser's* zone, and which day
 * a lock was taken on is a fact about the creditor, not about the reader.
 */

export const EM_DASH = '—';
export const EN_DASH = '–';

/* ── money ─────────────────────────────────────────────────────────────── */

/**
 * Add one to a non-negative digit string.
 *
 * A carry loop rather than `Number(x) + 1`, because the whole-dollar part of a
 * loan figure can exceed what a float holds exactly and this file's entire
 * premise is that money never becomes a number.
 */
function addOne(digits) {
  const out = String(digits || '0').split('');
  let index = out.length - 1;
  while (index >= 0) {
    if (out[index] === '9') {
      out[index] = '0';
      index -= 1;
    } else {
      out[index] = String(Number(out[index]) + 1);
      return out.join('');
    }
  }
  return `1${out.join('')}`;
}

/**
 * Format a decimal string as currency without ever converting it to a number.
 *
 * `precision: 'dollar'` rounds half-up to the nearest whole dollar, which is
 * what §1026.37(o)(4)(i)(A) requires of almost every figure on a **Loan
 * Estimate**. It is a display rule: the value on the wire stays exact, and the
 * PDF exporter applies the same rule from the same `form.money_precision`, so
 * the screen and the paper cannot disagree.
 *
 * `dashNegative` uses the en dash the printed forms set a subtraction with —
 * "– $500" — rather than a hyphen.
 *
 * @param {string|null|undefined} value e.g. "286.40", "-1200.00"
 * @param {{ fallback?: string, showSign?: boolean, precision?: 'cent'|'dollar',
 *           dashNegative?: boolean }} [options]
 */
export function formatMoney(value, options = {}) {
  const {
    fallback = EM_DASH,
    showSign = false,
    precision = 'cent',
    dashNegative = false,
  } = options;
  if (value === null || value === undefined || value === '') return fallback;

  const raw = String(value).trim();
  const negative = raw.startsWith('-');
  const unsigned = negative ? raw.replace(/^-/, '') : raw;

  const [rawWhole = '0', decimals = ''] = unsigned.split('.');
  const cents = `${decimals}00`.slice(0, 2);

  let whole = rawWhole || '0';
  let tail = `.${cents}`;
  if (precision === 'dollar') {
    if (Number(cents) >= 50) whole = addOne(whole);
    tail = '';
  }

  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const sign = negative ? (dashNegative ? `${EN_DASH} ` : '-') : showSign ? '+' : '';
  return `${sign}$${grouped}${tail}`;
}

/**
 * Is this money string greater than zero? String comparison, no float maths.
 * Used for "is a cure still owed" — a question the UI must not get wrong.
 */
export function isPositiveMoney(value) {
  if (value === null || value === undefined || value === '') return false;
  const raw = String(value).trim();
  if (raw.startsWith('-')) return false;
  return /[1-9]/.test(raw);
}

/** Signed variance for the Comparison grid — "+$200.00" reads as an increase. */
export function formatVariance(value, options = {}) {
  if (value === null || value === undefined || value === '') {
    return options.fallback ?? EM_DASH;
  }
  return formatMoney(value, { ...options, showSign: true });
}

/**
 * A money string as a whole number of cents, by string surgery.
 *
 * The file's rule is that money never becomes a float, and this does not break
 * it: cents are integers, and an integer is the one thing a JS number holds
 * exactly. `"1295.00"` → `129500`. Returns null for anything unparseable, which
 * the callers below treat as "cannot say" rather than as zero.
 */
function toCents(value) {
  if (value === null || value === undefined || value === '') return null;
  const raw = String(value).trim();
  const negative = raw.startsWith('-');
  const [whole = '0', fraction = ''] = (negative ? raw.slice(1) : raw).split('.');
  const digits = `${whole.replace(/\D/g, '')}${`${fraction}00`.slice(0, 2)}`;
  if (!digits || !/^\d+$/.test(digits)) return null;
  const cents = Number(digits);
  if (!Number.isSafeInteger(cents)) return null;
  return negative ? -cents : cents;
}

/**
 * How far a charge rose above what was promised, as a percentage.
 *
 * Display only, and only ever alongside the two figures it is derived from —
 * "+$200.00 · up 15.4%". The ratio is the one place a division happens, and it
 * is rounded for reading rather than reported to the cent, so it can never be
 * mistaken for the figure that is owed.
 *
 * Returns null when the question does not arise: no baseline, a baseline of
 * zero (every increase off nothing is infinite, which tells a reader nothing),
 * or a charge that did not rise.
 */
export function formatIncreasePercent(baseline, delta) {
  const base = toCents(baseline);
  const rise = toCents(delta);
  if (base === null || base <= 0 || rise === null || rise <= 0) return null;
  const percent = (rise / base) * 100;
  if (percent < 0.1) return '<0.1%';
  const rounded = percent >= 10 ? Math.round(percent) : Math.round(percent * 10) / 10;
  return `${rounded}%`;
}

/** Did this line rise above its baseline? String-safe, no float maths. */
export function isIncrease(delta) {
  return isPositiveMoney(delta);
}

/* ── dates ─────────────────────────────────────────────────────────────── */

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * `"2026-08-18"` → `"Aug 18, 2026"`.
 *
 * Parsed by splitting rather than `new Date(str)`: the ISO-date form is treated
 * as UTC midnight by the JS engine, so anywhere west of Greenwich it renders as
 * the previous day. A disclosure dated one day early is a compliance finding.
 */
export function formatTridDate(value, fallback = EM_DASH) {
  if (!value) return fallback;
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return fallback;
  return `${MONTHS[month - 1]} ${day}, ${year}`;
}

/** Short form for dense rails and table headers: `"Aug 18"`. */
export function formatTridDateShort(value, fallback = EM_DASH) {
  if (!value) return fallback;
  const [, month, day] = String(value).slice(0, 10).split('-').map(Number);
  if (!month || !day) return fallback;
  return `${MONTHS[month - 1]} ${day}`;
}

/**
 * Whole days from today until a deadline. Negative means it has passed.
 * Returns null when there is no deadline — which is not the same as zero.
 */
export function daysUntil(value, today = new Date()) {
  if (!value) return null;
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  const target = new Date(year, month - 1, day);
  const from = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((target - from) / 86400000);
}

/** "Due in 3 days" / "Overdue by 2 days" / "Due today". */
export function formatDueLabel(value) {
  const days = daysUntil(value);
  if (days === null) return null;
  if (days === 0) return 'Due today';
  if (days > 0) return `Due in ${days} day${days === 1 ? '' : 's'}`;
  const overdue = Math.abs(days);
  return `Overdue by ${overdue} day${overdue === 1 ? '' : 's'}`;
}

/* ── tri-state ─────────────────────────────────────────────────────────── */

/**
 * Render a genuinely tri-state boolean without collapsing `null` into `false`.
 *
 * @example triState(entry.has_prepayment_penalty) // "Not recorded" when null
 */
export function triState(value, labels = {}) {
  const { yes = 'Yes', no = 'No', unknown = 'Not recorded' } = labels;
  if (value === true) return yes;
  if (value === false) return no;
  return unknown;
}

/**
 * The changed-circumstance verdict on a revised LE.
 *
 * `null` — no circumstance was cited, so there was nothing to be timely about.
 * `false` — one was cited but went out late, so it reset no baseline.
 * These are different facts and the design shows them differently.
 */
export function formatCcValid(value) {
  return triState(value, {
    yes: 'Timely',
    no: 'Untimely — resets nothing',
    unknown: 'No circumstance cited',
  });
}

/** Section C only enters the 10% pot when the provider was on the written list. */
export function formatWrittenList(value) {
  return triState(value, {
    yes: 'On the written list',
    no: 'Off the written list',
    unknown: 'Not confirmed',
  });
}

/** APR is a string too — `null` is "not recorded", not "0%". */
export function formatApr(value, fallback = 'Not recorded') {
  if (value === null || value === undefined || value === '') return fallback;
  return `${String(value).trim()}%`;
}

/* ── worksheet lock ────────────────────────────────────────────────────── */

/**
 * Can fee lines be edited right now, derived from the ledger?
 *
 * Mirrors `worksheet_lock.py` exactly — three states:
 * no disclosures → editable (nothing has been promised to anyone yet) ·
 * a draft open → editable (that is what a draft is for) ·
 * everything issued → locked.
 *
 * Derived client-side so the worksheet can grey itself out immediately rather
 * than letting the user type into a field that will 409. The server check still
 * runs; a stale tab must handle the 409 anyway.
 */
export function deriveWorksheetLock(ledger) {
  const disclosures = ledger?.disclosures ?? [];
  if (disclosures.length === 0) {
    return { editable: true, reason: 'no_disclosures', draft: null, lastIssued: null };
  }

  const draft = disclosures.find((d) => d.status === 'draft') ?? null;
  const issued = disclosures.filter(
    (d) => d.status === 'issued' || d.status === 'superseded'
  );
  const lastIssued = issued.length ? issued[issued.length - 1] : null;

  if (draft) {
    return { editable: true, reason: 'draft_open', draft, lastIssued };
  }
  return { editable: false, reason: 'all_issued', draft: null, lastIssued };
}

/* ── errors ────────────────────────────────────────────────────────────── */

/**
 * Pull a user-facing message out of an RTK Query error.
 *
 * The disclosure endpoints return `409` with a `detail` string written for a
 * user, not a developer — "A Closing Disclosure was provided on Sep 5, 2026; no
 * revised Loan Estimate may follow it." Surface those directly rather than
 * replacing them with something vaguer.
 */
export function disclosureErrorMessage(error, fallback = 'Something went wrong.') {
  const data = error?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
  /* A structured refusal. The batch worksheet save answers with
     `{message, code, failed:{...}}` under `detail`, so the row that stopped it
     can be pointed at — and without this line the whole body fell past the
     string test above and every one of those became "Something went wrong."
     The sentence is always first in that object for exactly this reason. */
  if (typeof data?.detail?.message === 'string' && data.detail.message.trim()) {
    return data.detail.message;
  }
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  if (typeof error?.error === 'string' && error.error.trim()) return error.error;
  return fallback;
}
